import { useState } from "react";

import {
  localizedShareCopy,
  validatedShareIntentUrl,
  type ShareChannel,
  type ShareIntentUrls,
  type ShareLocale,
  type SharePlacement,
} from "@/share";
import { openExternalNoopener } from "@/share-client";

import "./ShareOpportunity.css";

interface ShareLinkResponse {
  shareUrl: string;
  title: string;
  text: string;
  intents: ShareIntentUrls;
}

interface ShareOpportunityProps {
  locale: ShareLocale;
  opportunitySlug: string;
  companyName: string;
  placement: SharePlacement;
  className?: string;
  onShared?: (detail: { channel: ShareChannel; placement: SharePlacement }) => void;
}

type ExternalShareChannel = "x" | "linkedin" | "whatsapp";
type ShareState = {
  status: "idle" | "opening" | "copying" | "copied" | "opened" | "error";
  channel?: ExternalShareChannel;
};

async function copyShareUrl(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const field = document.createElement("textarea");
  field.value = value;
  field.readOnly = true;
  field.style.position = "fixed";
  field.style.opacity = "0";
  document.body.appendChild(field);
  field.select();
  const copied = document.execCommand("copy");
  field.remove();
  if (!copied) throw new Error("clipboard_unavailable");
}

async function requestShareLink(
  locale: ShareLocale,
  opportunitySlug: string,
  placement: SharePlacement,
  channel: ExternalShareChannel | "copy",
): Promise<ShareLinkResponse> {
  const response = await fetch("/api/share-links", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ locale, opportunitySlug, placement, channel }),
  });
  const result = (await response.json().catch(() => null)) as ShareLinkResponse | null;
  if (!response.ok || !result?.shareUrl || !result.title || !result.text || !result.intents?.x) {
    throw new Error("share_link_unavailable");
  }
  return result;
}

export default function ShareOpportunity({
  locale,
  opportunitySlug,
  companyName,
  placement,
  className,
  onShared,
}: ShareOpportunityProps) {
  const copy = localizedShareCopy(locale, companyName, placement);
  const [state, setState] = useState<ShareState>({ status: "idle" });
  const busy = state.status === "opening" || state.status === "copying";

  const shareExternally = async (channel: ExternalShareChannel) => {
    setState({ status: "opening", channel });
    try {
      const result = await requestShareLink(locale, opportunitySlug, placement, channel);
      const intent = validatedShareIntentUrl(channel, result.intents[channel], result.shareUrl);
      openExternalNoopener(intent);
      setState({ status: "opened", channel });
      onShared?.({ channel, placement });
    } catch {
      setState({ status: "error", channel });
    }
  };

  const copyLink = async () => {
    setState({ status: "copying" });
    try {
      const result = await requestShareLink(locale, opportunitySlug, placement, "copy");
      await copyShareUrl(result.shareUrl);
      setState({ status: "copied" });
      onShared?.({ channel: "copy", placement });
    } catch {
      setState({ status: "error" });
    }
  };

  const status =
    state.status === "opening"
      ? state.channel === "x" ? copy.openingX : copy.openingShare
      : state.status === "copying"
        ? copy.copying
      : state.status === "copied"
        ? copy.copied
        : state.status === "opened"
          ? state.channel === "x" ? copy.xOpened : copy.shareOpened
          : state.status === "error"
            ? copy.error
            : "";

  return (
    <section
      className={["share-opportunity", className].filter(Boolean).join(" ")}
      aria-label={copy.regionLabel}
      aria-busy={busy}
    >
      <strong className="share-opportunity__prompt">{copy.prompt}</strong>
      <div className="share-opportunity__actions">
        <button
          className="share-opportunity__button share-opportunity__button--x"
          type="button"
          disabled={busy}
          onClick={() => void shareExternally("x")}
        >
          {state.status === "opening" && state.channel === "x" ? copy.openingX : copy.shareOnX} ↗
        </button>
        <button
          className="share-opportunity__button share-opportunity__button--linkedin"
          type="button"
          disabled={busy}
          onClick={() => void shareExternally("linkedin")}
        >
          {copy.shareOnLinkedIn} ↗
        </button>
        <button
          className="share-opportunity__button share-opportunity__button--whatsapp"
          type="button"
          disabled={busy}
          onClick={() => void shareExternally("whatsapp")}
        >
          {copy.shareOnWhatsApp} ↗
        </button>
        <button
          className="share-opportunity__button share-opportunity__button--copy"
          type="button"
          disabled={busy}
          onClick={() => void copyLink()}
        >
          {state.status === "copying" ? copy.copying : copy.copyLink}
        </button>
      </div>
      <p className="share-opportunity__privacy">{copy.privacy}</p>
      <p className="share-opportunity__status" role="status" aria-live="polite">
        {status}
      </p>
    </section>
  );
}
